import action from "../helpers/rest-client";
import { SignInData, SignUpData } from "../types/auth.dto";

export class ErrorAuth extends Error { }

let publicKeyRequest: Promise<any> | null = null;
let publicKeyResponse: any = null;

class Auth {
  public getToken() {
    return localStorage.getItem("token");
  }

  public async refreshToken() {
    const token = localStorage.getItem("token");

    if (!token) {
      return;
    }

    return action
      .Get({
        url: "/token",
      })
      .then((resp) => {
        const { token, user } = resp;
        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(user));
        return resp.user;
      })
      .catch((error) => {
        throw new ErrorAuth(error.message);
      });
  }

  public getTokenExpirationTime() {
    const token = localStorage.getItem("token");

    if (token) {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return new Date(payload.exp * 1000);
    }
  }

  public getTokenRemainingTime() {
    const tokenExpirationTime = this.getTokenExpirationTime();

    if (tokenExpirationTime) {
      const now = new Date();
      return tokenExpirationTime.getTime() - now.getTime();
    }

    return 0;
  }

  public async getAccess() {
    return action
      .Get({
        url: "/access",
      })
      .then((resp) => {
        return resp.success;
      })
      .catch((error) => {
        throw new ErrorAuth(error.message);
      });
  }

  public async userAuthenticated(callback) {
    const tokenRemainingTime = this.getTokenRemainingTime();
    let isAutenticated = false;

    if (tokenRemainingTime) {
      const fiveMinutes = 1000 * 60 * 5;
      isAutenticated = tokenRemainingTime > fiveMinutes;
    }

    if (isAutenticated) {
      try {
        const accessData = await this.getAccess();
        if (callback) {
          callback(true, accessData);
        }
      } catch (error) {
        if (callback) {
          callback(false);
        }
      }
    } else {
      if (callback) {
        callback(false);
      }
    }
  }

  public async signUp(payload: SignUpData) {
    return action
      .Post({
        url: "/register",
        body: payload,
      })
      .then((resp) => {
        return resp;
      })
      .catch((error) => {
        throw new ErrorAuth(error.message);
      });
  }

  public async getPublicKey() {
    if (publicKeyResponse) {
      return publicKeyResponse;
    }

    if (!publicKeyRequest) {
      publicKeyRequest = action
        .Get({
          url: "/key",
        })
        .then((resp) => {
          publicKeyResponse = resp;
          return resp;
        })
        .catch((error) => {
          publicKeyRequest = null;
          throw new ErrorAuth(error.message);
        });
    }

    return publicKeyRequest;
  }

  public async signIn(payload: SignInData) {
    const { encryptPayload } = await import("../utils/encryption");

    const cleanPayload = (Object.keys(payload) as (keyof SignInData)[]).reduce(
      (acc, key) => {
        const value = payload[key];

        if (value !== undefined && value !== "") {
          acc[key] = value;
        }

        return acc;
      },
      {} as SignInData
    );

    const encrypted = await encryptPayload(cleanPayload);
    const body = encrypted ? { payload: encrypted } : cleanPayload;

    return action
      .Post({
        url: "/login",
        body,
      })
      .then((resp) => {
        const { token, user, browser_hash } = resp.success?.result || {};
        const httpCode = resp.httpCode || resp.status;

        if (token && user) {
          localStorage.setItem("token", token);
          localStorage.setItem("user", JSON.stringify(user));

          if (browser_hash) {
            localStorage.setItem("browserHash", browser_hash);
          }
        }

        return {
          ...resp,
          user,
          token,
          browserHash: browser_hash,
          httpCode,
          message: resp.message,
        };
      })
      .catch((error) => {
        const responseData = error?.response?.data || {};
        const apiError = responseData?.error || {};
        const apiPayload = apiError?.payload || responseData?.payload || {};
        const httpCode = error?.response?.status || error?.httpCode || error?.status || 401;
        const message =
          apiError?.message ||
          responseData?.message ||
          error?.message ||
          "Error en el inicio de sesión";
        const isFirstLogin =
          Boolean(apiPayload?.isFirstLogin) ||
          Boolean(responseData?.isFirstLogin) ||
          Boolean(error?.isFirstLogin);
        const passwordChangeRequired =
          Boolean(apiPayload?.passwordExpired) ||
          Boolean(apiPayload?.password_expired) ||
          Boolean(apiPayload?.isPasswordExpired) ||
          Boolean(apiPayload?.passwordChangeRequired) ||
          Boolean(apiPayload?.password_change_required) ||
          Boolean(apiPayload?.requiresPasswordChange) ||
          Boolean(apiPayload?.mustChangePassword) ||
          isFirstLogin ||
          apiError?.message === "PASSWORD_CHANGE_REQUIRED" ||
          responseData?.message === "PASSWORD_CHANGE_REQUIRED";

        throw Object.assign(new ErrorAuth(message), {
          httpCode,
          message,
          code: apiError?.code || responseData?.code || error?.code,
          payload: apiPayload || error?.payload,
          errors: apiError?.errors || error?.errors,
          qrCodeUrl: apiPayload?.qrCodeUrl,
          secret: apiPayload?.secret,
          isFirstLogin,
          passwordChangeRequired,
          isPasswordExpired: passwordChangeRequired,
        });
      });
  }

  public signOut() {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    // Only remove browserHash if device wasn't set to be remembered
    const rememberDevice = localStorage.getItem("rememberDevice");
    if (rememberDevice !== "true") {
      localStorage.removeItem("browserHash");
    }
  }

  /**
   * Revoke the current session on the server.
   * Sends POST /logout-session.
   * If credentials are provided, it allows logout during a session conflict (unauthenticated).
   * Otherwise, it requires an existing Bearer token.
   */
  public async logoutSession(credentials?: { email?: string; password?: string }) {
    const token = this.getToken();
    if (!token && !credentials) return;

    let body = {};
    if (credentials) {
      const { encryptPayload } = await import("../utils/encryption");
      const encrypted = await encryptPayload(credentials);
      body = encrypted ? { payload: encrypted } : credentials;
    }

    return action
      .Post({
        url: "/logout-session",
        body,
      })
      .then((resp) => resp)
      .catch(() => {
        // Best-effort: ignore server errors on logout
      });
  }

  public recoverPassword(payload) {
    return action
      .Post({
        url: "/forgot",
        body: payload,
      })
      .then((resp) => {
        return resp.success;
      })
      .catch((error) => {
        // Extract error information from axios error structure
        const httpCode = error.response?.status || error.httpCode || error.status || 500;
        const errorData = error.response?.data || {};
        const message = errorData.error?.message || error.message || "Error al recuperar contraseña";

        throw Object.assign(new ErrorAuth(message), {
          httpCode,
          message,
        });
      });
  }

  public async resetPassword(payload) {
    const { encryptPayload } = await import("../utils/encryption");
    const { password, ...rest } = payload;
    const encrypted = await encryptPayload({ password });
    const body = encrypted ? { ...rest, payload: encrypted } : payload;

    return action
      .Post({
        url: "/reset",
        body,
      })
      .then((resp) => {
        return resp.user;
      })
      .catch((error) => {
        // Extract error information from axios error structure
        const httpCode = error.response?.status || 500;
        const errorData = error.response?.data || {};
        const message = errorData.error?.message || error.message || "Error al restablecer contraseña";

        throw Object.assign(new ErrorAuth(message), {
          httpCode,
          message,
        });
      });
  }
}

const auth = new Auth();
export default auth;
